import { Component, DestroyRef, inject } from '@angular/core';
import { AvatarModule, ComponentsModule, SidenavModule } from '@mastechnicaegroup/carelinelive-ui';
import { NavigationEnd, Router, RouterLink, RouterLinkActive } from '@angular/router';
import { AuthenticationService } from '@lib/auth/authentication.service';
import { AsyncPipe, NgIf, NgOptimizedImage } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { filter } from 'rxjs';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { SettingsService } from '@lib/setting/settings.service';
import { successfulResult } from '@mastechnicaegroup/carelinelive-cdk/rx';
import { animate, style, transition, trigger } from '@angular/animations';

interface NavLink {
    label: string;
    route: string;
    icon?: string;
    nav: string;
    isAdminOnly?: boolean;
}

@Component({
    selector: 'app-navigation',
    templateUrl: './navigation.component.html',
    styleUrl: './navigation.component.scss',
    standalone: true,
    animations: [
        trigger('fadeMenu', [
          transition(':enter', [
            style({ opacity: 0 }),
            animate('200ms 50ms ease-in', style({ opacity: 1 }))
          ]),
          transition(':leave', [
            style({ opacity: 1, position: 'absolute', width: '100%' }),
            animate('200ms ease-out', style({ opacity: 0 }))
          ])
        ])
    ],
    imports: [
        SidenavModule,
        RouterLink,
        NgIf,
        AsyncPipe,
        AvatarModule,
        ComponentsModule,
        FormsModule,
        RouterLinkActive,
        NgOptimizedImage
    ]
})
export class NavigationComponent {
    protected readonly auth = inject(AuthenticationService);
    private readonly _router = inject(Router);
    private readonly _destroyRef = inject(DestroyRef);
    private readonly _settingsService = inject(SettingsService);
    protected readonly settings = this._settingsService.getSettingsResult();
    protected readonly settings$ = this.settings.result$.pipe(
        successfulResult()
    );

    protected user = toSignal(this.auth.user$);

    showSettingsNav = false;
    showCareNav = false;
    showPackagesNav = false;

    manageNavLinks: { label: string; route: string; icon?: string }[] = [
        { label: 'Users', route: '/users', icon: 'person' },
        { label: 'Clients', route: '/clients', icon: 'group' },
        { label: 'Commissioners', route: '/commissioners', icon: 'how_to_reg' },
        { label: 'Regions', route: '/regions', icon: 'place' },
        { label: 'Providers', route: '/providers', icon: 'volunteer_activism' }
    ];

    settingsNavLinks: NavLink[] = [
        { label: 'General information', route: '/settings/general-information', icon: '', nav: 'settings' },
        { label: 'Care options', route: '/settings/care-options', icon: '', nav: 'settings' },
        { label: 'Handback reasons', route: '/settings/handback-reasons', icon: '', nav: 'settings' },
        { label: 'Positions', route: '/settings/positions', icon: '', nav: 'settings' },
        { label: 'System admins', route: '/settings/system-admins', icon: '', nav: 'settings' },
        { label: 'Data upload', route: '/settings/data-upload', icon: '', nav: 'settings' },
        { label: 'API', route: '/settings/api', icon: '', nav: 'settings', isAdminOnly: true }
    ];

    get filteredSettingsNavLinks(): NavLink[] {
        const isAdministrator = this.user()?.permissions?.permission_name === 'Administrator';

        return this.settingsNavLinks.filter((link) => !link.isAdminOnly || isAdministrator);
    }

    careNavLinks: NavLink[] = [
        { label: 'Care types', route: '/settings/care-types', icon: '', nav: 'care' },
        { label: 'Care options', route: '/settings/care-options', icon: '', nav: 'care' }
    ];

    packagesNavLinks: NavLink[] = [
        { label: 'My packages', route: '/my-packages', icon: '', nav: 'packages' },
        { label: 'Potential packages', route: '/potential-packages', icon: '', nav: 'packages' }
    ];

    constructor(private router: Router) {
        this.setupNavigationState();
    }

    private setupNavigationState(): void {
        this._router.events.pipe(
            filter(event => event instanceof NavigationEnd),
            takeUntilDestroyed(this._destroyRef)
        ).subscribe(() => {
            this.updateNavigationState(this._router.url);
        });
    }

    private updateNavigationState(url: string): void {
        const path = this.router.url.split('?')[0];

        this.showSettingsNav = path.includes('settings') && !path.includes('care');
        this.showCareNav = path.includes('care') && !url.includes('q=');
        this.showPackagesNav = path.includes('packages');
    }

    protected logout(): void {
        this.auth.logout().subscribe(() => {
            this._router.navigate(['/auth/login']);
        });
    }

    logLinkClick(route: string): void {
        this.showSettingsNav = false;
        this.showPackagesNav = false;
        this.showCareNav = false;

        switch (route.replace('/', '')) {
            case 'settings':
                this.showSettingsNav = true;
                break;
            case 'packages':
                this.showPackagesNav = true;
                break;
            case 'care':
                this.showCareNav = true;
                break;
        }
    }

    settingsNav() {
        this._router.navigate(['/settings/general-information']);
    }
}
